/**
 * Effective-permission store for the *current* user.
 *
 * Holds the resolved permission document from GET /auth/permissions/me and
 * exposes it to nav + page gating (via usePermission). Fails open to `null`
 * (legacy module behavior preserved) when the endpoint is unavailable, so an
 * older backend never locks anyone out.
 */
import { create } from 'zustand';
import { getMyEffective } from '../api/permissionsApi';
import type { EffectivePermissions } from '../lib/permissionCatalog';

interface PermissionState {
  effective: EffectivePermissions | null;
  loaded: boolean;
  loading: boolean;
  fetchEffective: () => Promise<void>;
  /** Background re-read (INTRO-17): like `fetchEffective`, but a failure keeps
   *  the document we have instead of falling back to legacy gating. */
  refreshEffective: () => Promise<boolean>;
  reset: () => void;
}

export const usePermissionStore = create<PermissionState>((set, get) => ({
  effective: null,
  loaded: false,
  loading: false,

  fetchEffective: async () => {
    if (get().loading) return;
    set({ loading: true });
    try {
      const effective = await getMyEffective();
      set({ effective, loaded: true, loading: false });
    } catch {
      // Fail open: leave effective null so legacy module gating applies.
      set({ effective: null, loaded: true, loading: false });
    }
  },

  refreshEffective: async () => {
    if (get().loading) return false;
    set({ loading: true });
    try {
      const effective = await getMyEffective();
      set({ effective, loaded: true, loading: false });
      return true;
    } catch {
      set({ loading: false });
      return false;
    }
  },

  reset: () => set({ effective: null, loaded: false, loading: false }),
}));
